import Vue from 'vue';

/**
 * The dashboard shared worker's port, published as a singleton on Vue.prototype by dashboardSetup.
 * Undefined when the app fell back to direct hubs (no SharedWorker support). The single source of
 * truth every caller should read from — an instance-level `this.$workerPort` resolves through the
 * same prototype chain today, but reading it directly here means a future instance-level override
 * can't split callers into disagreeing about which port is current.
 */
export function getWorkerPort(): MessagePort | undefined {
    return (Vue.prototype as { $workerPort?: MessagePort }).$workerPort;
}

/**
 * Posts to the dashboard shared worker. Takes no `this` — it is absent entirely when the app fell
 * back to direct hubs, which is a no-op rather than an error.
 *
 * Note this cannot confirm delivery: postMessage to a port whose worker has been torn down is a
 * spec'd silent no-op, and only an uncloneable payload throws. Callers that need the release to
 * happen must not treat a quiet return as proof it did.
 */
export function postToWorkerPort(type: string, payload: unknown = null): void {
    const workerPort = getWorkerPort();
    if (!workerPort) return;

    try {
        workerPort.postMessage([type, payload]);
    } catch (error) {
        console.warn(`Failed to post ${type} to shared worker:`, error);
    }
}
